import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, Building2, MapPin, Sun, Battery, Copy, 
  Zap, Plus, Edit, Sparkles, Wrench, FileText, ExternalLink, Calendar as CalendarIcon, AlertTriangle, Trash2, MoreHorizontal, X, Eye, MessageSquare, Inbox, Send, ChevronDown, ChevronUp, Clock, Loader2, Filter, User, Package, ShoppingBag,
  Camera, Image, StickyNote, Upload, Ticket as TicketIcon, Download, FolderOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, parse } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SearchSelect from '../components/SearchSelect';
import CcSelector from '../components/CcSelector';
import AddressAutocomplete from '../components/AddressAutocomplete';
import api, { customerAPI, cleaningAPI, installationAPI, propertyAPI, notificationAPI, installationOfferteAPI, installationFactuurAPI, offerteAPI, factuurAPI, documentAPI } from '../lib/api';
import { formatDate, formatAddress, getGoogleMapsUrl, getWazeUrl, getInstallationTypeBadgeClass, getInstallationTypeLabel, needsCleaning, formatPhoneNumber } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const copyAddressText = (text) => {
  const done = () => toast.success(`Adres gekopieerd: ${text}`);
  const fallback = () => {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); done(); } catch { toast.error('Kopiëren mislukt'); }
    document.body.removeChild(ta);
  };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(done).catch(fallback);
  } else fallback();
};
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import PdfViewer from '../components/PdfViewer';
import { sanitizeHtml } from '../lib/sanitize';
import TicketCreateDialog from '../components/TicketCreateDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Installation Form Component
const InstallationForm = ({ installation, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState(installation || {
    installation_type: 'solar_panels',
    installation_date: '',
    panel_brand: '',
    panel_wattage: '',
    number_of_panels: '',
    inverter_brand: '',
    inverter_model: '',
    battery_brand: '',
    battery_capacity: '',
    charger_brand: '',
    charger_power: '',
    notes: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Clean up the data - remove empty strings and convert to proper types
    const cleanedData = {};
    for (const [key, value] of Object.entries(formData)) {
      if (value !== '' && value !== null && value !== undefined) {
        cleanedData[key] = value;
      }
    }
    onSubmit(cleanedData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Type Installatie *</Label>
        <Select value={formData.installation_type} onValueChange={(v) => handleChange('installation_type', v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="solar_panels">Zonnepanelen</SelectItem>
            <SelectItem value="battery">Batterij</SelectItem>
            <SelectItem value="ev_charger">Laadpaal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Datum Plaatsing</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start text-left font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formData.installation_date ? format(parse(formData.installation_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={formData.installation_date ? parse(formData.installation_date, 'yyyy-MM-dd', new Date()) : undefined}
              onSelect={(date) => date && handleChange('installation_date', format(date, 'yyyy-MM-dd'))}
              locale={nl}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {formData.installation_type === 'solar_panels' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Merk Panelen</Label>
              <Input
                value={formData.panel_brand || ''}
                onChange={(e) => handleChange('panel_brand', e.target.value)}
                placeholder="bv. DMEGC, SunPower"
              />
            </div>
            <div className="space-y-2">
              <Label>Aantal Panelen</Label>
              <Input
                type="number"
                value={formData.number_of_panels || ''}
                onChange={(e) => handleChange('number_of_panels', parseInt(e.target.value) || '')}
                placeholder="bv. 10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Vermogen per Paneel (Wp)</Label>
            <Input
              type="number"
              value={formData.panel_wattage || ''}
              onChange={(e) => handleChange('panel_wattage', parseInt(e.target.value) || '')}
              placeholder="bv. 400"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Merk Omvormer</Label>
              <Input
                value={formData.inverter_brand || ''}
                onChange={(e) => handleChange('inverter_brand', e.target.value)}
                placeholder="bv. GoodWe, SMA"
              />
            </div>
            <div className="space-y-2">
              <Label>Model Omvormer</Label>
              <Input
                value={formData.inverter_model || ''}
                onChange={(e) => handleChange('inverter_model', e.target.value)}
                placeholder="bv. 3.6 kW"
              />
            </div>
          </div>
        </>
      )}

      {formData.installation_type === 'battery' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Merk Batterij</Label>
            <Input
              value={formData.battery_brand || ''}
              onChange={(e) => handleChange('battery_brand', e.target.value)}
              placeholder="bv. Tesla, LG"
            />
          </div>
          <div className="space-y-2">
            <Label>Capaciteit (kWh)</Label>
            <Input
              value={formData.battery_capacity || ''}
              onChange={(e) => handleChange('battery_capacity', e.target.value)}
              placeholder="bv. 13.5"
            />
          </div>
        </div>
      )}

      {formData.installation_type === 'ev_charger' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Merk Laadpaal</Label>
            <Input
              value={formData.charger_brand || ''}
              onChange={(e) => handleChange('charger_brand', e.target.value)}
              placeholder="bv. Wallbox, Alfen"
            />
          </div>
          <div className="space-y-2">
            <Label>Vermogen (kW)</Label>
            <Input
              value={formData.charger_power || ''}
              onChange={(e) => handleChange('charger_power', e.target.value)}
              placeholder="bv. 11 kW"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Notities</Label>
        <Textarea
          value={formData.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
          placeholder="Extra opmerkingen..."
        />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuleren
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Opslaan...' : (installation?.id ? 'Bijwerken' : 'Toevoegen')}
        </Button>
      </DialogFooter>
    </form>
  );
};

const PropertyCard = ({ property, onClick, onEditInstallation, onDeleteInstallation, onAddInstallation, onDeleteProperty, onEditProperty, onSetCleaningContract, onCreateSchema, hasRole, onDeletePurchasedProduct }) => {
  const hasInstallations = property.installations && property.installations.length > 0;
  const lastCleaningDate = property.last_cleaning?.cleaning_date;
  const needsClean = needsCleaning(lastCleaningDate);

  return (
    <Card 
      className="property-card" 
      onClick={onClick}
      data-testid={`property-card-${property.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-blue-500/20 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-blue-400" />
            </div>
            <div
              className="cursor-pointer hover:text-primary transition-colors"
              title="Klik om adres te kopiëren"
              data-testid={`copy-address-${property.id}`}
              onClick={(e) => {
                e.stopPropagation();
                copyAddressText(`${property.street || ''} ${property.number || ''}, ${property.postal_code || ''} ${property.city || ''}`.replace(/\s+/g, ' ').trim());
              }}>
              <p className="font-medium text-sm">{property.street} {property.number}</p>
              <p className="text-xs text-muted-foreground">{property.postal_code} {property.city}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" title="Navigatie">
                  <MapPin className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Navigeren naar</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <a 
                    href={getGoogleMapsUrl(property)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center cursor-pointer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Google Maps
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a 
                    href={getWazeUrl(property)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center cursor-pointer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Waze
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  data-testid={`menu-copy-address-${property.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyAddressText(`${property.street || ''} ${property.number || ''}, ${property.postal_code || ''} ${property.city || ''}`.replace(/\s+/g, ' ').trim());
                  }}>
                  <Copy className="w-4 h-4 mr-2" />
                  Adres kopiëren
                </DropdownMenuItem>
                {hasRole(['admin', 'sales']) && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditProperty(property); }}>
                    <Edit className="w-4 h-4 mr-2" />
                    Bewerken
                  </DropdownMenuItem>
                )}
                {hasRole(['admin']) && (
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onCreateSchema(property); }} data-testid={`create-schema-${property.id}`}>
                    <Zap className="w-4 h-4 mr-2" />
                    Schema maken
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground">Reinigingscontract</DropdownMenuLabel>
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); onSetCleaningContract(property.id, '2_yearly'); }}
                  className={property.cleaning_contract === '2_yearly' ? 'bg-primary/10' : ''}
                >
                  <Sparkles className="w-4 h-4 mr-2 text-amber-500" />
                  2-jaarlijks {property.cleaning_contract === '2_yearly' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); onSetCleaningContract(property.id, '3_yearly'); }}
                  className={property.cleaning_contract === '3_yearly' ? 'bg-primary/10' : ''}
                >
                  <Sparkles className="w-4 h-4 mr-2 text-blue-500" />
                  3-jaarlijks {property.cleaning_contract === '3_yearly' && '✓'}
                </DropdownMenuItem>
                {property.cleaning_contract && (
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); onSetCleaningContract(property.id, null); }}
                    className="text-muted-foreground"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Contract verwijderen
                  </DropdownMenuItem>
                )}
                {hasRole('admin') && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); onDeleteProperty(property.id); }}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Verwijderen
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Cleaning Contract Badge */}
        {property.cleaning_contract && (
          <div className="mb-2">
            <Badge 
              variant="outline" 
              className={property.cleaning_contract === '2_yearly' ? 'border-amber-500 text-amber-500' : 'border-blue-500 text-blue-500'}
            >
              <Sparkles className="w-3 h-3 mr-1" />
              {property.cleaning_contract === '2_yearly' ? '2-jaarlijks' : '3-jaarlijks'} contract
            </Badge>
          </div>
        )}

        {/* Installations with full details */}
        <div className="space-y-3">
          {hasInstallations ? (
            property.installations.map((inst) => (
              <div key={inst.id} className="text-xs border-t border-border/50 pt-2 first:border-0 first:pt-0 group">
                <div className="flex items-center justify-between mb-1">
                  <Badge 
                    variant="outline" 
                    className={getInstallationTypeBadgeClass(inst.installation_type)}
                  >
                    {inst.installation_type === 'solar_panels' && <Sun className="w-3 h-3 mr-1" />}
                    {inst.installation_type === 'battery' && <Battery className="w-3 h-3 mr-1" />}
                    {inst.installation_type === 'ev_charger' && <Zap className="w-3 h-3 mr-1" />}
                    {getInstallationTypeLabel(inst.installation_type)}
                  </Badge>
                  {hasRole(['admin', 'technician']) && (
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={(e) => { e.stopPropagation(); onEditInstallation(inst, property.id); }}
                        title="Bewerken"
                      >
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); onDeleteInstallation(inst.id); }}
                        title="Verwijderen"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="text-muted-foreground space-y-0.5 pl-1">
                  {inst.installation_type === 'solar_panels' && (
                    <>
                      {inst.panel_brand && <p>Merk panelen: <span className="text-foreground">{inst.panel_brand}</span></p>}
                      {inst.number_of_panels && <p>Aantal panelen: <span className="text-foreground">{inst.number_of_panels}</span></p>}
                      {inst.panel_wattage && <p>Vermogen: <span className="text-foreground">{inst.panel_wattage} Wp</span></p>}
                      {inst.number_of_panels && inst.panel_wattage && (
                        <p>Totaal: <span className="text-foreground font-medium">{(inst.number_of_panels * inst.panel_wattage / 1000).toFixed(2)} kWp</span></p>
                      )}
                      {(inst.inverter_brand || inst.inverter_model) && (
                        <p>Omvormer: <span className="text-foreground">{[inst.inverter_brand, inst.inverter_model].filter(Boolean).join(' ')}</span></p>
                      )}
                    </>
                  )}
                  {inst.installation_type === 'battery' && (
                    <>
                      {inst.battery_brand && <p>Merk: <span className="text-foreground">{inst.battery_brand}</span></p>}
                      {inst.battery_capacity && <p>Capaciteit: <span className="text-foreground">{inst.battery_capacity}{!String(inst.battery_capacity).toLowerCase().includes('kwh') ? ' kWh' : ''}</span></p>}
                    </>
                  )}
                  {inst.installation_type === 'ev_charger' && (
                    <>
                      {inst.charger_brand && <p>Merk: <span className="text-foreground">{inst.charger_brand}</span></p>}
                      {inst.charger_power && <p>Vermogen: <span className="text-foreground">{inst.charger_power}</span></p>}
                    </>
                  )}
                  {inst.installation_date && (
                    <p className="flex items-center gap-1">
                      <CalendarIcon className="w-3 h-3" />
                      Geplaatst: <span className="text-foreground">{formatDate(inst.installation_date)}</span>
                    </p>
                  )}
                </div>
              </div>
            ))
          ) : (
            <span className="text-xs text-muted-foreground">Geen installaties</span>
          )}

          {/* Add Installation Button */}
          {hasRole(['admin', 'technician']) && (
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-2 text-xs h-7"
              onClick={(e) => { e.stopPropagation(); onAddInstallation(property.id); }}
            >
              <Plus className="w-3 h-3 mr-1" />
              Installatie Toevoegen
            </Button>
          )}

          {/* Purchased Products */}
          {property.purchased_products && property.purchased_products.length > 0 && (
            <div className="pt-3 mt-3 border-t border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <ShoppingBag className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium">Gekochte Producten</span>
                <Badge variant="outline" className="text-xs">
                  {property.purchased_products.length}
                </Badge>
              </div>
              <div className="space-y-2">
                {property.purchased_products.map((product, idx) => (
                  <div key={product.id || idx} className="text-xs bg-muted/30 rounded-lg p-2 group/pp relative">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{product.name}</span>
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground">x{product.quantity}</span>
                        {hasRole && hasRole('admin') && product.id && onDeletePurchasedProduct && (
                          <button
                            className="opacity-0 group-hover/pp:opacity-100 text-destructive hover:bg-destructive/10 rounded p-0.5 transition-opacity"
                            title="Product verwijderen"
                            onClick={(e) => { e.stopPropagation(); onDeletePurchasedProduct(property.id, product); }}
                            data-testid={`delete-purchased-product-${product.id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    </div>
                    {product.brand && (
                      <p className="text-muted-foreground">Merk: {product.brand}</p>
                    )}
                    {product.power_wp && (
                      <p className="text-muted-foreground">Vermogen: {product.power_wp} Wp</p>
                    )}
                    {product.capacity_kwh && (
                      <p className="text-muted-foreground">Capaciteit: {product.capacity_kwh} kWh</p>
                    )}
                    {product.source_factuur_number && (
                      <p className="text-muted-foreground text-[10px] mt-1">
                        Factuur: {product.source_factuur_number}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Last Cleaning */}
          <div className="pt-2 border-t border-border/50">
            <div className="flex items-center gap-2 text-xs">
              <Sparkles className="w-3 h-3 text-purple-400" />
              {lastCleaningDate ? (
                <span className={needsClean ? 'text-amber-400' : 'text-muted-foreground'}>
                  Laatste reiniging: <span className="text-foreground">{formatDate(lastCleaningDate)}</span>
                  {needsClean && (
                    <span className="ml-2 text-amber-400 inline-flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Reiniging nodig
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Nog nooit gereinigd
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = ['properties', 'documents', 'klantcontact'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'properties';
  const { hasRole, user } = useAuth();
  const [data, setData] = useState(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cleaningDialogOpen, setCleaningDialogOpen] = useState(false);
  const [savingCleaning, setSavingCleaning] = useState(false);
  const [cleaningForm, setCleaningForm] = useState({
    property_id: '',
    cleaning_date: '',
    cleaning_time: '09:00',
    performed_by: '',
    notes: ''
  });
  const [cleaningEmailAction, setCleaningEmailAction] = useState('queue'); // 'queue', 'send', or 'none'
  const [cleaningCcEmails, setCleaningCcEmails] = useState([]); // CC recipients for cleaning confirmation
  
  // Installation editing state
  const [installationDialogOpen, setInstallationDialogOpen] = useState(false);
  const [editInstallation, setEditInstallation] = useState(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [savingInstallation, setSavingInstallation] = useState(false);
  
  // Customer editing state
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerFormData, setCustomerFormData] = useState(null);
  const [kboLoading, setKboLoading] = useState(false);

  const handleKboLookup = async () => {
    setKboLoading(true);
    try {
      const res = await api.get('/lookup/vat', { params: { vat: customerFormData.vat_number } });
      const d = res.data;
      setCustomerFormData(prev => ({
        ...prev,
        vat_number: d.vat || prev.vat_number,
        company_name: d.company_name || prev.company_name,
        billing_street: d.street || prev.billing_street,
        billing_postal_code: d.postal_code || prev.billing_postal_code,
        billing_city: d.city || prev.billing_city,
      }));
      if (d.street) setHasBillingAddress(true);
      toast.success(`Bedrijfsgegevens opgehaald: ${d.company_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'BTW-nummer niet gevonden in KBO/VIES');
    } finally {
      setKboLoading(false);
    }
  };
  const [hasBillingAddress, setHasBillingAddress] = useState(false);

  // Property (address) adding state
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [propertyForm, setPropertyForm] = useState({
    street: '',
    number: '',
    postal_code: '',
    city: '',
    country: 'Belgium',
    notes: ''
  });
  
  // Property (address) editing state
  const [editPropertyDialogOpen, setEditPropertyDialogOpen] = useState(false);
  const [editPropertyForm, setEditPropertyForm] = useState(null);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null); // { type: 'installation' | 'property', id: string, name: string }

  // Duplicate detection state
  const [duplicates, setDuplicates] = useState([]);

  // PDF Preview state
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewTitle, setPdfPreviewTitle] = useState('');

  // Email/Klantcontact state
  const [customerEmails, setCustomerEmails] = useState([]);
  const [emailContacts, setEmailContacts] = useState({ contacts: [], inbound_count: 0, total_emails: 0 });
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [selectedEmailFilter, setSelectedEmailFilter] = useState('all');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDetailLoading, setEmailDetailLoading] = useState(false);

  // Camera/Photo state
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [instPhotos, setInstPhotos] = useState([]);
  const [selectedInstPhotos, setSelectedInstPhotos] = useState(new Set());
  const [pushingPhotos, setPushingPhotos] = useState(false);
  const [thanksDialogOpen, setThanksDialogOpen] = useState(false);
  const [sendingThanks, setSendingThanks] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const toggleInstPhoto = (photoId) => {
    setSelectedInstPhotos(prev => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId); else next.add(photoId);
      return next;
    });
  };

  const pushPhotosToSharepoint = async () => {
    const photos = instPhotos.filter(p => selectedInstPhotos.has(p.id));
    if (!photos.length) return;
    setPushingPhotos(true);
    try {
      const res = await api.post(`/customers/${id}/installation-photos/to-sharepoint`, { photos });
      toast.success(`${res.data.added} foto('s) toegevoegd aan de klantmap onder "Foto's Installatie"`);
      setSelectedInstPhotos(new Set());
      setThanksDialogOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon foto\'s niet naar de klantmap pushen');
    } finally {
      setPushingPhotos(false);
    }
  };

  const sendThanksMail = async () => {
    setSendingThanks(true);
    try {
      await api.post(`/customers/${id}/send-installation-thanks`);
      toast.success('Bedankt mail klaargezet voor verzending');
      setThanksDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon bedankt mail niet klaarzetten');
    } finally {
      setSendingThanks(false);
    }
  };

  useEffect(() => {
    api.get(`/customers/${id}/installation-photos`)
      .then(r => setInstPhotos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setInstPhotos([]));
  }, [id]);

  const downloadInstPhoto = async (p) => {
    try {
      let blob;
      if (p.url?.startsWith('http')) {
        const res = await fetch(p.url);
        blob = await res.blob();
      } else {
        const res = await api.get(p.url.replace(/^\/api/, ''), { responseType: 'blob' });
        blob = res.data;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = p.filename || 'installatiefoto.jpg';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Download mislukt'); }
  };
  const [photoUploading, setPhotoUploading] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState(null); // For lightbox view
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null); // Separate ref for gallery selection

  // Notes state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [customerNotes, setCustomerNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const fetchData = async () => {
    try {
      const response = await customerAPI.getDetails(id);
      setData(response.data);
      
      // Check for duplicates related to this customer
      checkDuplicates(response.data.customer);
    } catch (error) {
      toast.error('Kon klantgegevens niet laden');
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const checkDuplicates = async (customer) => {
    try {
      const response = await notificationAPI.getAll(false, 100);
      const duplicateNotifications = response.data.filter(n => 
        n.type === 'duplicate_customer' && 
        n.data?.customers?.some(c => c.id === customer.id || c.id === id)
      );
      
      // Extract other customers that are duplicates
      const otherDuplicates = [];
      duplicateNotifications.forEach(notification => {
        notification.data?.customers?.forEach(c => {
          if (c.id !== id && !otherDuplicates.find(d => d.id === c.id)) {
            otherDuplicates.push({
              ...c,
              duplicate_type: notification.data.duplicate_type,
              duplicate_value: notification.data.duplicate_value
            });
          }
        });
      });
      
      setDuplicates(otherDuplicates);
    } catch (error) {
      console.error('Failed to check duplicates:', error);
    }
  };

  // Fetch customer emails
  const fetchCustomerEmails = async (senderFilter = null) => {
    setEmailsLoading(true);
    try {
      let url = `/customers/${id}/emails?limit=100`;
      if (senderFilter && senderFilter !== 'all') {
        url += `&internal_sender=${encodeURIComponent(senderFilter)}`;
      }
      const response = await customerAPI.request('get', url);
      setCustomerEmails(response.data);
    } catch (error) {
      console.error('Error fetching customer emails:', error);
    } finally {
      setEmailsLoading(false);
    }
  };

  // Fetch customer photos
  const fetchCustomerPhotos = async () => {
    try {
      const response = await documentAPI.getAll({ entity_type: 'customer', entity_id: id });
      // Filter for photos only
      const photos = (response.data || []).filter(d => d.document_type === 'photo');
      setCustomerPhotos(photos);
    } catch (error) {
      setCustomerPhotos([]);
    }
  };

  // Handle photo capture/upload - supports multiple files
  const handlePhotoCapture = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !data?.customer) return;

    setPhotoUploading(true);
    const totalFiles = files.length;
    let successCount = 0;
    let failCount = 0;
    
    try {
      // Upload all files in parallel
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('entity_type', 'customer');
          formData.append('entity_id', id);
          formData.append('document_type', 'photo');
          formData.append('description', `Foto - ${data.customer.first_name} ${data.customer.last_name} - ${new Date().toLocaleDateString('nl-BE')}`);
          
          await documentAPI.upload(formData);
          successCount++;
          return { success: true };
        } catch (error) {
          failCount++;
          console.error('Upload failed for file:', file.name, error);
          return { success: false, filename: file.name };
        }
      });
      
      await Promise.all(uploadPromises);
      
      // Show appropriate toast message
      if (successCount > 0 && failCount === 0) {
        toast.success(`${successCount} foto${successCount > 1 ? "'s" : ''} toegevoegd!`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`${successCount} foto's geüpload, ${failCount} mislukt`);
      } else {
        toast.error('Kon foto\'s niet uploaden');
      }
      
      // Refresh photos
      await fetchCustomerPhotos();
    } catch (error) {
      toast.error('Kon foto\'s niet uploaden');
    } finally {
      setPhotoUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Delete photo
  const deletePhoto = async (photoId) => {
    try {
      await documentAPI.delete(photoId);
      toast.success('Foto verwijderd');
      setCustomerPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (error) {
      toast.error('Kon foto niet verwijderen');
    }
  };

  // Save customer notes
  const saveCustomerNotes = async () => {
    setSavingNotes(true);
    try {
      await customerAPI.update(id, { notes: customerNotes });
      toast.success('Notities opgeslagen');
      setNotesDialogOpen(false);
      // Update local data
      setData(prev => ({
        ...prev,
        customer: { ...prev.customer, notes: customerNotes }
      }));
    } catch (error) {
      toast.error('Kon notities niet opslaan');
    } finally {
      setSavingNotes(false);
    }
  };

  // Open camera dialog
  const openCameraDialog = () => {
    fetchCustomerPhotos();
    setCameraDialogOpen(true);
  };

  // Open notes dialog
  const openNotesDialog = () => {
    setCustomerNotes(data?.customer?.notes || '');
    setNotesDialogOpen(true);
  };

  // Fetch email contacts (who communicated with this customer)
  const fetchEmailContacts = async () => {
    try {
      const response = await customerAPI.request('get', `/customers/${id}/email-contacts`);
      setEmailContacts(response.data);
    } catch (error) {
      console.error('Error fetching email contacts:', error);
    }
  };

  // Fetch single email detail
  const fetchEmailDetail = async (emailId) => {
    setEmailDetailLoading(true);
    try {
      const response = await customerAPI.request('get', `/customers/${id}/emails/${emailId}`);
      setSelectedEmail(response.data);
    } catch (error) {
      toast.error('Kon email niet laden');
    } finally {
      setEmailDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, navigate]);

  // Registreer als recent bekeken klant (voor sortering op Klanten-pagina)
  useEffect(() => {
    if (!id) return;
    try {
      const recent = JSON.parse(localStorage.getItem('recent_customers') || '[]').filter(r => r !== id);
      recent.unshift(id);
      localStorage.setItem('recent_customers', JSON.stringify(recent.slice(0, 10)));
    } catch { /* noop */ }
  }, [id]);

  // Fetch emails when filter changes
  useEffect(() => {
    if (id) {
      fetchCustomerEmails(selectedEmailFilter);
      fetchEmailContacts();
    }
  }, [id, selectedEmailFilter]);

  // Customer edit handlers
  const handleEditCustomer = () => {
    if (data?.customer) {
      setCustomerFormData({
        first_name: data.customer.first_name || '',
        last_name: data.customer.last_name || '',
        salutation: data.customer.salutation || '',
        email: data.customer.email || '',
        additional_emails: data.customer.additional_emails || [],
        phone: data.customer.phone || '',
        company_name: data.customer.company_name || '',
        is_business: !!data.customer.is_business,
        attn: data.customer.attn || '',
        billing_street: data.customer.billing_street || '',
        billing_postal_code: data.customer.billing_postal_code || '',
        billing_city: data.customer.billing_city || '',
        billing_country: data.customer.billing_country || 'België',
        language: data.customer.language || 'nl',
        vat_number: data.customer.vat_number || '',
        sales_person: data.customer.sales_person || '',
        notes: data.customer.notes || ''
      });
      setHasBillingAddress(!!data.customer.billing_street);
      setEditCustomerDialogOpen(true);
    }
  };

  const handleSaveCustomer = async () => {
    if (!customerFormData) return;
    setSavingCustomer(true);
    try {
      await customerAPI.update(id, {
        ...customerFormData,
        additional_emails: (customerFormData.additional_emails || []).map(e => e.trim()).filter(e => e && e.includes('@')),
        ...(hasBillingAddress ? {} : { billing_street: '', billing_postal_code: '', billing_city: '', billing_country: '' })
      });
      toast.success('Klant bijgewerkt');
      setEditCustomerDialogOpen(false);
      fetchData();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects)
      const detail = error.response?.data?.detail;
      let errorMsg = 'Kon klant niet bijwerken';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic returns array of {type, loc, msg, input, ctx}
        errorMsg = detail.map(e => e.msg || e.message).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setSavingCustomer(false);
    }
  };

  // Installation handlers
  const handleAddInstallation = (propertyId) => {
    setSelectedPropertyId(propertyId);
    setEditInstallation(null);
    setInstallationDialogOpen(true);
  };

  const handleEditInstallation = (installation, propertyId) => {
    setSelectedPropertyId(propertyId);
    setEditInstallation(installation);
    setInstallationDialogOpen(true);
  };

  const handleSaveInstallation = async (formData) => {
    setSavingInstallation(true);
    try {
      if (editInstallation?.id) {
        await installationAPI.update(editInstallation.id, { ...formData, property_id: selectedPropertyId });
        toast.success('Installatie bijgewerkt');
      } else {
        await installationAPI.create({ ...formData, property_id: selectedPropertyId });
        toast.success('Installatie toegevoegd');
      }
      setInstallationDialogOpen(false);
      setEditInstallation(null);
      fetchData(); // Refresh customer data
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : 'Kon installatie niet opslaan');
      toast.error(errorMessage);
    } finally {
      setSavingInstallation(false);
    }
  };

  const handleDeleteInstallation = async (installationId) => {
    setItemToDelete({ type: 'installation', id: installationId, name: 'deze installatie' });
    setDeleteConfirmOpen(true);
  };

  // Property (address) handlers
  const handleAddProperty = async (e) => {
    e.preventDefault();
    setSavingProperty(true);
    try {
      await propertyAPI.create({
        customer_id: id,
        ...propertyForm
      });
      toast.success('Adres toegevoegd!');
      setPropertyDialogOpen(false);
      setPropertyForm({ street: '', number: '', postal_code: '', city: '', country: 'Belgium', notes: '' });
      fetchData(); // Refresh customer data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon adres niet toevoegen');
    } finally {
      setSavingProperty(false);
    }
  };

  const handleCreateSchema = async (property) => {
    try {
      const cust = data?.customer;
      const custName = `${cust?.first_name || ''} ${cust?.last_name || ''}`.trim() || cust?.company_name || 'Schema';
      const res = await api.post('/schemas', { name: custName, customer_id: cust?.id || id, property_id: property.id });
      navigate(`/schemas/${res.data.id}`);
    } catch (e) {
      toast.error('Kon schema niet aanmaken');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    const property = data?.properties?.find(p => p.id === propertyId);
    const propertyName = property ? `${property.street} ${property.number}` : 'dit adres';
    setItemToDelete({ type: 'property', id: propertyId, name: propertyName });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'installation') {
        await installationAPI.delete(itemToDelete.id);
        toast.success('Installatie verwijderd');
      } else if (itemToDelete.type === 'property') {
        await propertyAPI.delete(itemToDelete.id);
        toast.success('Adres verwijderd');
      }
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMsg = itemToDelete.type === 'installation' 
        ? 'Kon installatie niet verwijderen' 
        : 'Kon adres niet verwijderen';
      toast.error(error.response?.data?.detail || errorMsg);
    } finally {
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const handleEditProperty = (property) => {
    setEditPropertyForm({
      id: property.id,
      street: property.street || '',
      number: property.number || '',
      postal_code: property.postal_code || '',
      city: property.city || '',
      country: property.country || 'Belgium',
      notes: property.notes || ''
    });
    setEditPropertyDialogOpen(true);
  };

  const handleSetCleaningContract = async (propertyId, contractType) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties/${propertyId}/cleaning-contract?contract_type=${contractType || ''}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const label = contractType === '2_yearly' ? '2-jaarlijks' : contractType === '3_yearly' ? '3-jaarlijks' : 'verwijderd';
      toast.success(`Reinigingscontract ${label}`);
      fetchData();
    } catch (error) {
      toast.error('Kon contract niet bijwerken');
    }
  };

  const handleSaveEditProperty = async () => {
    if (!editPropertyForm) return;
    setSavingProperty(true);
    try {
      await propertyAPI.update(editPropertyForm.id, editPropertyForm);
      toast.success('Adres bijgewerkt');
      setEditPropertyDialogOpen(false);
      fetchData();
    } catch (error) {
      // Handle Pydantic validation errors (array of objects)
      const detail = error.response?.data?.detail;
      let errorMsg = 'Kon adres niet bijwerken';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setSavingProperty(false);
    }
  };

  const handleScheduleCleaning = async (e) => {
    e.preventDefault();
    setSavingCleaning(true);
    try {
      const cleaningDateTime = cleaningForm.cleaning_time 
        ? `${cleaningForm.cleaning_date}T${cleaningForm.cleaning_time}`
        : cleaningForm.cleaning_date;
      
      // Create the cleaning
      const response = await cleaningAPI.create({
        property_id: cleaningForm.property_id,
        cleaning_date: cleaningDateTime,
        performed_by: cleaningForm.performed_by,
        notes: cleaningForm.notes
      });
      
      toast.success('Reiniging ingepland!');
      
      // If user wants to send/queue confirmation email
      const cleaningId = response?.data?.id || response?.id;
      if (cleaningEmailAction !== 'none' && cleaningId) {
        try {
          // Queue the email via CRM
          const emailResponse = await cleaningAPI.sendConfirmationEmail({
            cleaning_id: cleaningId,
            property_id: cleaningForm.property_id,
            cleaning_date: cleaningForm.cleaning_date,
            cleaning_time: cleaningForm.cleaning_time || '09:00',
            send_confirmation_email: true,
            cc_emails: cleaningCcEmails  // Include CC recipients
          });
          
          const emailId = emailResponse?.data?.email_id || emailResponse?.email_id;
          if (cleaningEmailAction === 'send' && emailId) {
            // Send the queued email immediately
            try {
              await fetch(`${API_URL}/api/email-queue/${emailId}/send`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                }
              });
              toast.success('Bevestigingsmail verzonden!');
            } catch (sendError) {
              console.error('Error sending email:', sendError);
              toast.success('Mail klaargezet in wachtrij');
            }
          } else {
            toast.success('Bevestigingsmail klaargezet in wachtrij');
          }
        } catch (emailError) {
          console.error('Error queueing confirmation email:', emailError);
          // Don't show error - the cleaning was still created successfully
        }
      }
      
      setCleaningDialogOpen(false);
      setCleaningForm({ property_id: '', cleaning_date: '', cleaning_time: '09:00', performed_by: '', notes: '' });
      setCleaningEmailAction('queue'); // Reset to default
      setCleaningCcEmails([]); // Reset CC emails
      // Refresh data
      fetchData();
    } catch (error) {
      toast.error('Kon reiniging niet inplannen');
    } finally {
      setSavingCleaning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-40 animate-pulse bg-muted rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 animate-pulse bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { customer, properties, documents, offertes, facturen } = data;
  
  // Count total documents
  const totalOffertes = (offertes?.reiniging?.length || 0) + (offertes?.installation?.length || 0);
  const totalFacturen = (facturen?.reiniging?.length || 0) + (facturen?.installation?.length || 0);

  // PDF Preview handlers
  const openPdfPreview = async (type, item) => {
    setPdfPreviewLoading(true);
    setPdfPreviewOpen(true);
    setPdfPreviewTitle(item.offerte_number || item.factuur_number);
    
    try {
      let response;
      if (type === 'installation_offerte') {
        response = await installationOfferteAPI.getPdf(item.id);
      } else if (type === 'installation_factuur') {
        response = await installationFactuurAPI.getPdf(item.id);
      } else if (type === 'reiniging_offerte') {
        response = await offerteAPI.getPdf(item.id);
      } else if (type === 'reiniging_factuur') {
        response = await factuurAPI.getPdf(item.id);
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
    } catch (error) {
      toast.error('Kon PDF niet laden');
      setPdfPreviewOpen(false);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    if (pdfPreviewUrl) {
      window.URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-full overflow-x-hidden px-2 sm:px-0" data-testid="customer-detail-page">
      {/* Back Button + Klantgegevens kopiëren */}
      <div className="flex items-center justify-between mb-2 sm:mb-4">
        <Button variant="ghost" onClick={() => navigate('/customers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Terug naar Klanten</span>
          <span className="sm:hidden">Terug</span>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="customer-copy-btn">
              <Copy className="w-4 h-4 mr-1.5" /> Kopieer gegevens
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(() => {
              const name = customer.company_name ? `${customer.company_name}${(customer.first_name || customer.last_name) ? ` - ${customer.first_name || ''} ${customer.last_name || ''}`.trim() : ''}` : `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
              const cp = (t, l) => { navigator.clipboard.writeText(t).then(() => toast.success(`${l} gekopieerd`)); };
              return (
                <>
                  <DropdownMenuItem data-testid="copy-detail-name" onClick={() => cp(name, 'Naam')}>Naam</DropdownMenuItem>
                  {customer.phone && <DropdownMenuItem data-testid="copy-detail-phone" onClick={() => cp(customer.phone, 'Telefoon')}>Telefoon</DropdownMenuItem>}
                  {customer.email && <DropdownMenuItem data-testid="copy-detail-email" onClick={() => cp(customer.email, 'E-mail')}>E-mail</DropdownMenuItem>}
                  {properties.map(p => (
                    <DropdownMenuItem key={p.id} data-testid={`copy-detail-address-${p.id}`}
                      onClick={() => cp(`${p.street || ''} ${p.number || ''}, ${p.postal_code || ''} ${p.city || ''}`.replace(/\s+/g, ' ').trim(), 'Adres')}>
                      {`${p.street || ''} ${p.number || ''}, ${p.city || ''}`}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem data-testid="copy-detail-all" onClick={() => cp([name, customer.phone, customer.email, ...properties.map(p => `${p.street || ''} ${p.number || ''}, ${p.postal_code || ''} ${p.city || ''}`)].filter(Boolean).join('\n'), 'Alles')}>
                    Alles kopiëren
                  </DropdownMenuItem>
                </>
              );
            })()}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Duplicate Warning */}
      {duplicates.length > 0 && (
        <Alert variant="destructive" className="border-orange-500/50 bg-orange-500/10">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <AlertTitle className="text-orange-500">Mogelijke dubbele klant</AlertTitle>
          <AlertDescription className="text-orange-400">
            <p className="mb-2">Deze klant heeft overeenkomende gegevens met andere klanten:</p>
            <div className="flex flex-wrap gap-2">
              {duplicates.map((dup) => (
                <Button
                  key={dup.id}
                  variant="outline"
                  size="sm"
                  className="text-xs border-orange-500/50 text-orange-500 hover:bg-orange-500/20"
                  onClick={() => navigate(`/klanten/${dup.id}`)}
                >
                  {dup.first_name} {dup.last_name}
                  <span className="ml-1 text-[10px] opacity-70">
                    ({dup.duplicate_type === 'email' ? 'email' : 'telefoon'})
                  </span>
                </Button>
              ))}
            </div>
            <p className="text-xs mt-2 opacity-70">Controleer of deze klanten samengevoegd moeten worden of dat één verwijderd moet worden.</p>
          </AlertDescription>
        </Alert>
      )}

      {/* Customer Info Card */}
      <Card className="border-border/50">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xl md:text-2xl font-bold text-primary">
                  {customer.first_name[0]}{customer.last_name[0]}
                </span>
              </div>
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                  {customer.first_name} {customer.last_name}
                </h1>
                {customer.company_name && (
                  <p className="text-muted-foreground text-sm">{customer.company_name}</p>
                )}
                {customer.attn && (
                  <p className="text-muted-foreground text-xs" data-testid="detail-attn">T.a.v. {customer.attn}</p>
                )}
                {customer.billing_street && (
                  <p className="text-muted-foreground text-xs" data-testid="detail-billing-address">
                    Facturatieadres: {customer.billing_street}, {[customer.billing_postal_code, customer.billing_city].filter(Boolean).join(' ')}{customer.billing_country && !['belgië', 'belgie', 'belgium', 'be'].includes(customer.billing_country.toLowerCase()) ? `, ${customer.billing_country}` : ''}
                  </p>
                )}
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-2 text-sm text-muted-foreground">
                  <div 
                    className="flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{customer.email || 'Geen email'}</span>
                  </div>
                  <a 
                    href={`tel:${customer.phone}`}
                    className="flex items-center gap-1 hover:text-green-400 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span className="hover:underline">{formatPhoneNumber(customer.phone) || 'Geen telefoon'}</span>
                  </a>
                </div>
              </div>
            </div>

            {hasRole(['admin', 'sales']) && (
              <>
              <Button 
                onClick={handleEditCustomer} 
                variant="outline"
                size="sm"
                className="w-fit"
                data-testid="detail-edit-customer-btn"
              >
                <Edit className="w-3 h-3 mr-1" />
                Klant Bewerken
              </Button>
              <Button 
                onClick={() => setTicketDialogOpen(true)} 
                variant="outline"
                size="sm"
                className="w-fit"
                data-testid="detail-create-ticket-btn"
              >
                <TicketIcon className="w-3 h-3 mr-1" />
                Ticket maken
              </Button>
              <TicketCreateDialog
                open={ticketDialogOpen}
                onOpenChange={setTicketDialogOpen}
                prefill={{ customer_id: customer.id }}
              />
              <Button 
                onClick={openCameraDialog} 
                variant="outline"
                size="sm"
                className="w-fit"
              >
                <Camera className="w-3 h-3 mr-1" />
                Foto's
              </Button>
              <Button 
                onClick={openNotesDialog} 
                variant="outline"
                size="sm"
                className="w-fit"
              >
                <StickyNote className="w-3 h-3 mr-1" />
                Notities
              </Button>
              </>
            )}
          </div>

          {customer.notes && (
            <div className="mt-4 p-3 rounded-md bg-secondary/50">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{customer.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setPropertyDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Adres Toevoegen
        </Button>
      </div>

      {/* Schedule Cleaning Dialog */}
      <Dialog open={cleaningDialogOpen} onOpenChange={setCleaningDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reiniging Inplannen</DialogTitle>
            <DialogDescription>Plan een reiniging in voor {customer.first_name} {customer.last_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleScheduleCleaning} className="space-y-4">
            <div className="space-y-2">
              <Label>Adres *</Label>
              <SearchSelect
                options={properties.map(p => ({
                  value: p.id,
                  label: `${p.street} ${p.number}, ${p.postal_code} ${p.city}`
                }))}
                value={cleaningForm.property_id}
                onChange={(value) => setCleaningForm(prev => ({ ...prev, property_id: value }))}
                placeholder="Zoek adres..."
                displayKey="label"
                valueKey="value"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Datum *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {cleaningForm.cleaning_date ? format(parse(cleaningForm.cleaning_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: nl }) : 'Selecteer datum'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={cleaningForm.cleaning_date ? parse(cleaningForm.cleaning_date, 'yyyy-MM-dd', new Date()) : undefined}
                      onSelect={(date) => date && setCleaningForm(prev => ({ ...prev, cleaning_date: format(date, 'yyyy-MM-dd') }))}
                      locale={nl}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Tijd *</Label>
                <Input
                  type="time"
                  value={cleaningForm.cleaning_time}
                  onChange={(e) => setCleaningForm(prev => ({ ...prev, cleaning_time: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Uitgevoerd door *</Label>
              <Input
                value={cleaningForm.performed_by}
                onChange={(e) => setCleaningForm(prev => ({ ...prev, performed_by: e.target.value }))}
                placeholder="Naam technicus"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={cleaningForm.notes}
                onChange={(e) => setCleaningForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Extra opmerkingen..."
              />
            </div>
            
            {/* Email Options */}
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-sm font-medium">Bevestigingsmail</Label>
              <div className="space-y-2">
                <div 
                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${cleaningEmailAction === 'queue' ? 'bg-blue-500/10 border-blue-500' : 'hover:bg-muted/50'}`}
                  onClick={() => setCleaningEmailAction('queue')}
                >
                  <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${cleaningEmailAction === 'queue' ? 'border-blue-500' : 'border-muted-foreground'}`}>
                    {cleaningEmailAction === 'queue' && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      Mail klaarzetten
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Mail komt in de wachtrij zodat je deze eerst kunt nakijken
                    </p>
                  </div>
                </div>
                
                <div 
                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${cleaningEmailAction === 'send' ? 'bg-green-500/10 border-green-500' : 'hover:bg-muted/50'}`}
                  onClick={() => setCleaningEmailAction('send')}
                >
                  <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${cleaningEmailAction === 'send' ? 'border-green-500' : 'border-muted-foreground'}`}>
                    {cleaningEmailAction === 'send' && <div className="w-2 h-2 rounded-full bg-green-500" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Send className="w-4 h-4 text-green-500" />
                      Mail direct verzenden
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Mail wordt meteen verstuurd naar de klant
                    </p>
                  </div>
                </div>
                
                <div 
                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${cleaningEmailAction === 'none' ? 'bg-muted/50 border-muted-foreground' : 'hover:bg-muted/50'}`}
                  onClick={() => setCleaningEmailAction('none')}
                >
                  <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${cleaningEmailAction === 'none' ? 'border-muted-foreground' : 'border-muted-foreground'}`}>
                    {cleaningEmailAction === 'none' && <div className="w-2 h-2 rounded-full bg-muted-foreground" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      Geen mail versturen
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* CC Selector - only show when email will be sent */}
            {cleaningEmailAction !== 'none' && (
              <CcSelector
                selectedEmails={cleaningCcEmails}
                onChange={setCleaningCcEmails}
                defaultEnabled={true}
              />
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCleaningDialogOpen(false)}>Annuleren</Button>
              <Button type="submit" disabled={savingCleaning || !cleaningForm.property_id}>
                {savingCleaning ? 'Opslaan...' : 'Inplannen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Property Dialog */}
      <Dialog open={propertyDialogOpen} onOpenChange={setPropertyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuw Adres Toevoegen</DialogTitle>
            <DialogDescription>Voeg een nieuw adres toe voor {customer.first_name} {customer.last_name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddProperty} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Straat *</Label>
                <AddressAutocomplete
                  value={propertyForm.street}
                  onChange={(v) => setPropertyForm(prev => ({ ...prev, street: v, street_fr: '' }))}
                  onSelect={(s) => setPropertyForm(prev => ({
                    ...prev,
                    street: s.street || prev.street,
                    number: s.number || prev.number,
                    postal_code: s.postal_code || prev.postal_code,
                    city: s.city || prev.city,
                    street_fr: s.street_fr || '',
                    city_fr: s.city_fr || '',
                    country: (s.country && s.country !== 'België') ? s.country : prev.country,
                  }))}
                  placeholder="Typ straatnaam..."
                  required
                  testId="new-property-street"
                  cityHint={propertyForm.city}
                  postcodeHint={propertyForm.postal_code}
                />
              </div>
              <div className="space-y-2">
                <Label>Nummer *</Label>
                <Input
                  value={propertyForm.number}
                  onChange={(e) => setPropertyForm(prev => ({ ...prev, number: e.target.value }))}
                  placeholder="Nr."
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Postcode *</Label>
                <Input
                  value={propertyForm.postal_code}
                  onChange={(e) => setPropertyForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  placeholder="1000"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Gemeente *</Label>
                <Input
                  value={propertyForm.city}
                  onChange={(e) => setPropertyForm(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Gemeente"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Land</Label>
              <Input
                value={propertyForm.country}
                onChange={(e) => setPropertyForm(prev => ({ ...prev, country: e.target.value }))}
                placeholder="Belgium"
              />
            </div>
            <div className="space-y-2">
              <Label>Notities</Label>
              <Textarea
                value={propertyForm.notes}
                onChange={(e) => setPropertyForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Extra opmerkingen..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPropertyDialogOpen(false)}>Annuleren</Button>
              <Button type="submit" disabled={savingProperty}>
                {savingProperty ? 'Opslaan...' : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList>
          <TabsTrigger value="properties" data-testid="tab-properties">
            Adressen ({properties.length})
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="w-4 h-4 mr-1" />
            Documenten ({totalOffertes + totalFacturen})
          </TabsTrigger>
          <TabsTrigger value="klantcontact" data-testid="tab-klantcontact">
            <MessageSquare className="w-4 h-4 mr-1" />
            Klantcontact {emailContacts.total_emails > 0 && `(${emailContacts.total_emails})`}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="properties" className="mt-6">
          {properties.length === 0 ? (
            <Card className="p-8 text-center">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nog geen adressen</p>
              <Button className="mt-4" onClick={() => setPropertyDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Eerste Adres Toevoegen
              </Button>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {properties.map((property) => (
                <PropertyCard 
                  key={property.id} 
                  property={property}
                  onClick={() => navigate(`/properties/${property.id}`)}
                  onEditInstallation={handleEditInstallation}
                  onDeleteInstallation={handleDeleteInstallation}
                  onAddInstallation={handleAddInstallation}
                  onDeleteProperty={handleDeleteProperty}
                  onEditProperty={handleEditProperty}
                  onSetCleaningContract={handleSetCleaningContract}
                  onCreateSchema={handleCreateSchema}
                  hasRole={hasRole}
                  onDeletePurchasedProduct={async (propId, product) => {
                    if (!window.confirm(`"${product.name}" verwijderen uit gekochte producten?`)) return;
                    try {
                      await api.delete(`/properties/${propId}/purchased-products/${product.id}`);
                      toast.success('Product verwijderd');
                      fetchData();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Verwijderen mislukt'); }
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-6 space-y-6">
          {/* Installatie foto's */}
          {instPhotos.length > 0 && (
            <Card data-testid="installation-photos-card">
              <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Camera className="w-5 h-5 text-blue-500" />
                  Installatie foto's ({instPhotos.length})
                </CardTitle>
                <Button size="sm" disabled={selectedInstPhotos.size === 0 || pushingPhotos}
                  onClick={pushPhotosToSharepoint} data-testid="push-inst-photos-sharepoint-btn">
                  {pushingPhotos ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FolderOpen className="w-4 h-4 mr-1.5" />}
                  {selectedInstPhotos.size > 0 ? `${selectedInstPhotos.size} naar klantmap` : 'Naar klantmap'}
                </Button>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Klik op een foto om groot te bekijken. Vink foto's aan om ze toe te voegen aan de klantmap onder "Foto's Installatie" (in het juiste project).
                </p>
                {[...new Set(instPhotos.map(p => p.project || 1))].sort((a, b) => a - b).map((projNum) => {
                  const projPhotos = instPhotos.filter(p => (p.project || 1) === projNum);
                  const multiProject = new Set(instPhotos.map(p => p.project || 1)).size > 1;
                  return (
                    <div key={projNum} className="mb-5 last:mb-0" data-testid={`inst-photos-project-${projNum}`}>
                      {multiProject && (
                        <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <FolderOpen className="w-4 h-4 text-orange-500" /> Project {projNum}
                          <span className="text-xs text-muted-foreground font-normal">({projPhotos.length} foto's)</span>
                        </p>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {projPhotos.map((p) => {
                          const src = p.url?.startsWith('http') ? p.url : `${API_URL}${p.url}`;
                          return (
                            <div key={p.id} className={`relative rounded-lg overflow-hidden border ${selectedInstPhotos.has(p.id) ? 'border-orange-500 ring-1 ring-orange-500' : 'bg-muted/30'}`}>
                              <input type="checkbox" checked={selectedInstPhotos.has(p.id)} onChange={() => toggleInstPhoto(p.id)}
                                className="absolute top-2 left-2 z-10 w-4 h-4 accent-orange-500 cursor-pointer"
                                data-testid={`select-inst-photo-${p.id}`} />
                              <button type="button" onClick={() => setLightboxPhoto(p)} className="block w-full" data-testid={`view-inst-photo-${p.id}`}>
                                <img src={src} alt={p.description || p.filename || 'Installatiefoto'} className="w-full h-28 object-cover" loading="lazy"
                                  onError={(e) => { e.target.style.objectFit = 'contain'; }} />
                              </button>
                              <div className="flex items-center justify-between px-2 py-1.5 bg-background">
                                <span className="text-[11px] truncate flex-1" title={p.description || p.filename || ''}>
                                  {p.description || p.filename || 'Foto'}
                                </span>
                                <button onClick={() => downloadInstPhoto(p)} className="p-1 text-muted-foreground hover:text-foreground"
                                  title="Downloaden" data-testid={`download-inst-photo-${p.id}`}>
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
          {/* Installatie Offertes */}
          {offertes?.installation?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Installatie Offertes ({offertes.installation.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {offertes.installation.map((offerte) => (
                    <div 
                      key={offerte.id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => openPdfPreview('installation_offerte', offerte)}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{offerte.offerte_number}</p>
                          <p className="text-sm text-muted-foreground">{offerte.title}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={offerte.status === 'accepted' ? 'default' : offerte.status === 'sent' ? 'secondary' : 'outline'}>
                          {offerte.status === 'accepted' ? 'Geaccepteerd' : offerte.status === 'sent' ? 'Verzonden' : 'Concept'}
                        </Badge>
                        <span className="font-semibold">€{(offerte.total_incl_vat || 0).toFixed(2)}</span>
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Installatie Facturen */}
          {facturen?.installation?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-green-500" />
                  Installatie Facturen ({facturen.installation.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {facturen.installation.map((factuur) => (
                    <div 
                      key={factuur.id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => openPdfPreview('installation_factuur', factuur)}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{factuur.factuur_number}</p>
                          {factuur.term_number && (
                            <p className="text-sm text-muted-foreground">Termijn {factuur.term_number} ({factuur.term_percentage}%)</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={factuur.status === 'paid' ? 'default' : factuur.status === 'sent' ? 'secondary' : 'outline'}
                          className={factuur.status === 'paid' ? 'bg-green-500' : ''}>
                          {factuur.status === 'paid' ? 'Betaald' : factuur.status === 'sent' ? 'Verzonden' : 'Concept'}
                        </Badge>
                        <span className="font-semibold">€{(factuur.total_incl_vat || 0).toFixed(2)}</span>
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reiniging Offertes */}
          {offertes?.reiniging?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Reiniging Offertes ({offertes.reiniging.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {offertes.reiniging.map((offerte) => (
                    <div 
                      key={offerte.id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => openPdfPreview('reiniging_offerte', offerte)}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{offerte.offerte_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={offerte.status === 'accepted' ? 'default' : offerte.status === 'sent' ? 'secondary' : 'outline'}>
                          {offerte.status === 'accepted' ? 'Geaccepteerd' : offerte.status === 'sent' ? 'Verzonden' : 'Concept'}
                        </Badge>
                        <span className="font-semibold">€{(offerte.total_incl_vat || 0).toFixed(2)}</span>
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reiniging Facturen */}
          {facturen?.reiniging?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-green-500" />
                  Reiniging Facturen ({facturen.reiniging.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {facturen.reiniging.map((factuur) => (
                    <div 
                      key={factuur.id} 
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => openPdfPreview('reiniging_factuur', factuur)}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{factuur.factuur_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={factuur.status === 'paid' ? 'default' : factuur.status === 'sent' ? 'secondary' : 'outline'}
                          className={factuur.status === 'paid' ? 'bg-green-500' : ''}>
                          {factuur.status === 'paid' ? 'Betaald' : factuur.status === 'sent' ? 'Verzonden' : 'Concept'}
                        </Badge>
                        <span className="font-semibold">€{(factuur.total_incl_vat || 0).toFixed(2)}</span>
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No documents message */}
          {totalOffertes === 0 && totalFacturen === 0 && (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nog geen offertes of facturen voor deze klant</p>
            </Card>
          )}
        </TabsContent>

        {/* Klantcontact Tab */}
        <TabsContent value="klantcontact" className="mt-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Email Communicatie
                  </CardTitle>
                  <CardDescription>
                    Bekijk alle email communicatie met deze klant
                  </CardDescription>
                </div>
                
                {/* Filter dropdown */}
                {emailContacts.contacts?.length > 0 && (
                  <Select value={selectedEmailFilter} onValueChange={setSelectedEmailFilter}>
                    <SelectTrigger className="w-[200px]">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter op medewerker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle medewerkers</SelectItem>
                      {emailContacts.contacts.map((contact) => (
                        <SelectItem key={contact.email} value={contact.email}>
                          {contact.name} ({contact.email_count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {emailsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : customerEmails.length === 0 ? (
                <div className="text-center py-12">
                  <Inbox className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">Geen emails gevonden</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Emails worden automatisch gesynchroniseerd wanneer er een email account is gekoppeld in Instellingen
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {customerEmails.map((email) => (
                    <div
                      key={email.id}
                      className={`p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors ${
                        selectedEmail?.id === email.id ? 'border-primary bg-muted/30' : ''
                      }`}
                      onClick={() => fetchEmailDetail(email.id)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          {/* Direction indicator */}
                          <div className={`p-2 rounded-full flex-shrink-0 ${
                            email.direction === 'outbound' 
                              ? 'bg-blue-500/10 text-blue-500' 
                              : 'bg-green-500/10 text-green-500'
                          }`}>
                            {email.direction === 'outbound' ? (
                              <Send className="w-4 h-4" />
                            ) : (
                              <Inbox className="w-4 h-4" />
                            )}
                          </div>
                          
                          <div className="min-w-0 flex-1">
                            {/* Sender info */}
                            <div className="flex items-center gap-2 mb-1">
                              {email.direction === 'outbound' ? (
                                <span className="font-medium text-sm">
                                  {email.internal_sender_name || email.internal_sender?.split('@')[0] || 'Team'}
                                </span>
                              ) : (
                                <span className="font-medium text-sm">
                                  {email.from_name || data?.customer?.first_name || 'Klant'}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {email.direction === 'outbound' ? '→ klant' : '→ team'}
                              </span>
                            </div>
                            
                            {/* Subject */}
                            <p className="font-medium text-sm truncate">{email.subject || '(Geen onderwerp)'}</p>
                            
                            {/* Preview */}
                            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                              {email.body_preview || '(Geen inhoud)'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Date and attachments */}
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-muted-foreground">
                            {new Date(email.email_date).toLocaleDateString('nl-NL', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(email.email_date).toLocaleTimeString('nl-NL', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          {email.has_attachments && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              <FileText className="w-3 h-3 mr-1" />
                              Bijlage
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded email content */}
                      {selectedEmail?.id === email.id && (
                        <div className="mt-4 pt-4 border-t">
                          {emailDetailLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin" />
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {/* Email metadata */}
                              <div className="text-sm space-y-1 text-muted-foreground">
                                <p><strong>Van:</strong> {selectedEmail.from_name} &lt;{selectedEmail.from_email}&gt;</p>
                                <p><strong>Aan:</strong> {selectedEmail.to_emails?.join(', ')}</p>
                                {selectedEmail.cc_emails?.length > 0 && (
                                  <p><strong>CC:</strong> {selectedEmail.cc_emails.join(', ')}</p>
                                )}
                              </div>
                              
                              {/* Email body */}
                              <div className="bg-muted/30 p-4 rounded-lg">
                                {selectedEmail.body_html ? (
                                  <div 
                                    className="prose prose-sm dark:prose-invert max-w-none"
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedEmail.body_html) }}
                                  />
                                ) : (
                                  <pre className="whitespace-pre-wrap text-sm font-sans">
                                    {selectedEmail.body_text || '(Geen inhoud)'}
                                  </pre>
                                )}
                              </div>
                              
                              {/* Attachments */}
                              {selectedEmail.attachments?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {selectedEmail.attachments.map((att, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      <FileText className="w-3 h-3 mr-1" />
                                      {att.filename} ({Math.round(att.size / 1024)}KB)
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Installation Dialog */}
      <Dialog open={installationDialogOpen} onOpenChange={setInstallationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editInstallation ? 'Installatie Bewerken' : 'Installatie Toevoegen'}</DialogTitle>
            <DialogDescription>
              {editInstallation ? 'Pas de installatiegegevens aan' : 'Voeg een nieuwe installatie toe aan dit adres'}
            </DialogDescription>
          </DialogHeader>
          <InstallationForm
            installation={editInstallation}
            onSubmit={handleSaveInstallation}
            onCancel={() => { setInstallationDialogOpen(false); setEditInstallation(null); }}
            loading={savingInstallation}
          />
        </DialogContent>
      </Dialog>

      {/* Customer Edit Dialog */}
      <Dialog open={editCustomerDialogOpen} onOpenChange={setEditCustomerDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Klant Bewerken</DialogTitle>
            <DialogDescription>Pas de klantgegevens aan</DialogDescription>
          </DialogHeader>
          {customerFormData && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Aanspreking</Label>
                <div className="flex gap-2">
                  {[['dhr', 'Dhr.'], ['mevr', 'Mevr.']].map(([val, lbl]) => (
                    <Button
                      key={val}
                      type="button"
                      size="sm"
                      variant={customerFormData.salutation === val ? 'default' : 'outline'}
                      onClick={() => setCustomerFormData(prev => ({ ...prev, salutation: prev.salutation === val ? '' : val }))}
                      data-testid={`edit-customer-salutation-${val}`}
                    >
                      {lbl}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Voornaam</Label>
                  <Input
                    value={customerFormData.first_name}
                    onChange={(e) => setCustomerFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Naam</Label>
                  <Input
                    value={customerFormData.last_name}
                    onChange={(e) => setCustomerFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={customerFormData.email}
                    onChange={(e) => setCustomerFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefoon</Label>
                  <Input
                    value={customerFormData.phone}
                    onChange={(e) => setCustomerFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
              </div>
              {/* Extra e-mailadressen */}
              <div className="space-y-2" data-testid="detail-additional-emails">
                <Label>Extra e-mailadressen</Label>
                {(customerFormData.additional_emails || []).map((em, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      type="email"
                      value={em}
                      onChange={(e) => setCustomerFormData(prev => {
                        const arr = [...(prev.additional_emails || [])];
                        arr[idx] = e.target.value;
                        return { ...prev, additional_emails: arr };
                      })}
                      placeholder="extra@voorbeeld.be"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setCustomerFormData(prev => ({
                        ...prev,
                        additional_emails: (prev.additional_emails || []).filter((_, i) => i !== idx)
                      }))}
                      title="Verwijderen"
                    >
                      <X className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCustomerFormData(prev => ({
                    ...prev,
                    additional_emails: [...(prev.additional_emails || []), '']
                  }))}
                  data-testid="detail-add-extra-email"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  E-mailadres toevoegen
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Bedrijf</Label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!customerFormData.is_business}
                        onChange={(e) => setCustomerFormData(prev => ({ ...prev, is_business: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 accent-orange-500"
                        data-testid="detail-customer-is-business"
                      />
                      <span className="text-xs font-medium">B2B klant</span>
                    </label>
                  </div>
                  <Input
                    value={customerFormData.company_name}
                    onChange={(e) => setCustomerFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  />
                  {customerFormData.is_business && (
                    <p className="text-xs text-muted-foreground">Op offertes/facturen: eerst bedrijfsnaam, daarna "T.a.v. {customerFormData.attn || `${customerFormData.first_name} ${customerFormData.last_name}`}"</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>BTW Nummer</Label>
                  <div className="flex gap-2">
                    <Input
                      value={customerFormData.vat_number}
                      onChange={(e) => setCustomerFormData(prev => ({ ...prev, vat_number: e.target.value }))}
                      data-testid="detail-customer-vat"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleKboLookup}
                      disabled={kboLoading || !(customerFormData.vat_number || '').trim()}
                      title="Bedrijfsgegevens ophalen uit KBO/VIES"
                      data-testid="detail-kbo-lookup-btn"
                    >
                      {kboLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'KBO'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Klik op KBO om officiële bedrijfsgegevens op te halen</p>
                </div>
              </div>
              {(customerFormData.is_business || customerFormData.company_name) && (
                <div className="space-y-2">
                  <Label>T.a.v. (contactpersoon op offerte/factuur)</Label>
                  <Input
                    value={customerFormData.attn || ''}
                    onChange={(e) => setCustomerFormData(prev => ({ ...prev, attn: e.target.value }))}
                    placeholder={`${customerFormData.first_name} ${customerFormData.last_name}`.trim() || 'Contactpersoon'}
                    data-testid="detail-customer-attn"
                  />
                </div>
              )}
              <div className="space-y-2 rounded-lg border border-border/50 p-3" data-testid="detail-customer-billing-section">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasBillingAddress}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setHasBillingAddress(v);
                      if (!v) {
                        setCustomerFormData(prev => ({ ...prev, billing_street: '', billing_postal_code: '', billing_city: '', billing_country: '' }));
                      } else {
                        setCustomerFormData(prev => ({ ...prev, billing_country: prev.billing_country || 'België' }));
                      }
                    }}
                    className="h-4 w-4 rounded border-gray-300 accent-orange-500"
                    data-testid="detail-customer-has-billing-address"
                  />
                  <span className="text-sm font-medium">Facturatieadres wijkt af van projectadres</span>
                </label>
                {hasBillingAddress && (
                  <div className="grid grid-cols-2 gap-4 pt-1">
                    <div className="space-y-2 col-span-2">
                      <Label>Straat + nr</Label>
                      <Input
                        value={customerFormData.billing_street || ''}
                        onChange={(e) => setCustomerFormData(prev => ({ ...prev, billing_street: e.target.value }))}
                        data-testid="detail-customer-billing-street"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Postcode</Label>
                      <Input
                        value={customerFormData.billing_postal_code || ''}
                        onChange={(e) => setCustomerFormData(prev => ({ ...prev, billing_postal_code: e.target.value }))}
                        data-testid="detail-customer-billing-postal"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Gemeente</Label>
                      <Input
                        value={customerFormData.billing_city || ''}
                        onChange={(e) => setCustomerFormData(prev => ({ ...prev, billing_city: e.target.value }))}
                        data-testid="detail-customer-billing-city"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Land</Label>
                      <Input
                        value={customerFormData.billing_country || ''}
                        onChange={(e) => setCustomerFormData(prev => ({ ...prev, billing_country: e.target.value }))}
                        placeholder="België"
                        data-testid="detail-customer-billing-country"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground col-span-2">Indien ingevuld wordt dit adres gebruikt op offertes en facturen i.p.v. het projectadres.</p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Verkoper</Label>
                <Input
                  value={customerFormData.sales_person}
                  onChange={(e) => setCustomerFormData(prev => ({ ...prev, sales_person: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Notities</Label>
                <Textarea
                  value={customerFormData.notes}
                  onChange={(e) => setCustomerFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditCustomerDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button onClick={handleSaveCustomer} disabled={savingCustomer} data-testid="detail-customer-save">
                  {savingCustomer ? 'Opslaan...' : 'Bijwerken'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Property Dialog */}
      <Dialog open={editPropertyDialogOpen} onOpenChange={setEditPropertyDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adres Bewerken</DialogTitle>
            <DialogDescription>Pas de adresgegevens aan</DialogDescription>
          </DialogHeader>
          {editPropertyForm && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Straat</Label>
                  <Input
                    value={editPropertyForm.street}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, street: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nummer</Label>
                  <Input
                    value={editPropertyForm.number}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, number: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Postcode</Label>
                  <Input
                    value={editPropertyForm.postal_code}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, postal_code: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gemeente</Label>
                  <Input
                    value={editPropertyForm.city}
                    onChange={(e) => setEditPropertyForm(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Land</Label>
                <Input
                  value={editPropertyForm.country}
                  onChange={(e) => setEditPropertyForm(prev => ({ ...prev, country: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Notities</Label>
                <Textarea
                  value={editPropertyForm.notes}
                  onChange={(e) => setEditPropertyForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditPropertyDialogOpen(false)}>
                  Annuleren
                </Button>
                <Button onClick={handleSaveEditProperty} disabled={savingProperty}>
                  {savingProperty ? 'Opslaan...' : 'Bijwerken'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {itemToDelete?.type === 'installation' ? 'Installatie verwijderen' : 'Adres verwijderen'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete?.type === 'installation' 
                ? `Weet je zeker dat je ${itemToDelete?.name} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`
                : `Weet je zeker dat je ${itemToDelete?.name} wilt verwijderen? Alle bijbehorende installaties worden ook verwijderd. Deze actie kan niet ongedaan worden gemaakt.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Preview Dialog */}
      <Dialog open={pdfPreviewOpen} onOpenChange={closePdfPreview}>
        <DialogContent className="max-w-4xl h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {pdfPreviewTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[60vh]">
            {pdfPreviewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2">PDF laden...</span>
              </div>
            ) : pdfPreviewUrl ? (
              <PdfViewer pdfUrl={pdfPreviewUrl} />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Geen PDF beschikbaar
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera/Photo Dialog */}
      <Dialog open={cameraDialogOpen} onOpenChange={setCameraDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Foto's - {data?.customer?.first_name} {data?.customer?.last_name}
            </DialogTitle>
            <DialogDescription>
              Maak een foto of bekijk bestaande foto's
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Hidden file input for camera capture */}
            <input
              type="file"
              ref={fileInputRef}
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/heic,image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              className="hidden"
            />
            
            {/* Hidden file input for gallery selection (no capture attribute) */}
            <input
              type="file"
              ref={galleryInputRef}
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/heic,image/*"
              multiple
              onChange={handlePhotoCapture}
              className="hidden"
            />
            
            {/* Action buttons - Camera and Gallery */}
            <div className="flex gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={photoUploading}
                className="flex-1"
              >
                {photoUploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {photoUploading ? 'Uploaden...' : 'Camera'}
              </Button>
              <Button
                onClick={() => galleryInputRef.current?.click()}
                disabled={photoUploading}
                variant="outline"
                className="flex-1"
              >
                <Upload className="w-4 h-4 mr-2" />
                Galerij
              </Button>
            </div>
            
            {/* Photos grid */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Bestaande Foto's ({customerPhotos.length})</h4>
              {customerPhotos.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded-lg">
                  Nog geen foto's voor deze klant
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto">
                  {customerPhotos.map((photo) => {
                    // Always use local API endpoint for reliable loading
                    // Google Drive URLs often have CORS/auth issues on mobile
                    const photoUrl = `${API_URL}/api/documents/${photo.id}/file`;
                    
                    return (
                    <div key={photo.id} className="relative rounded-lg overflow-hidden border bg-gray-100">
                      {photo.id ? (
                        <div 
                          className="w-full h-28 cursor-pointer"
                          onClick={() => setViewingPhoto(photoUrl)}
                        >
                          <img
                            src={photoUrl}
                            alt={photo.description || 'Klant foto'}
                            className="w-full h-full object-cover"
                            style={{ pointerEvents: 'none' }}
                            onError={(e) => { 
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400 text-xs">Foto niet beschikbaar</div>';
                            }}
                          />
                        </div>
                      ) : (
                        <div className="w-full h-28 flex items-center justify-center text-muted-foreground text-xs">
                          Foto niet beschikbaar
                        </div>
                      )}
                      <div className="flex items-center justify-between p-2 bg-white">
                        <p className="text-xs truncate flex-1">{photo.description || 'Foto'}</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); deletePhoto(photo.id); }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded ml-2"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCameraDialogOpen(false)}>
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Foto groot bekijken (lightbox) */}
      <Dialog open={!!lightboxPhoto} onOpenChange={(o) => { if (!o) setLightboxPhoto(null); }}>
        <DialogContent className="max-w-3xl" data-testid="inst-photo-lightbox">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-500" />
              {lightboxPhoto?.description || lightboxPhoto?.filename || 'Installatiefoto'}
            </DialogTitle>
          </DialogHeader>
          {lightboxPhoto && (
            <div className="space-y-3">
              <img src={lightboxPhoto.url?.startsWith('http') ? lightboxPhoto.url : `${API_URL}${lightboxPhoto.url}`}
                alt={lightboxPhoto.description || 'Installatiefoto'}
                className="w-full max-h-[65vh] object-contain rounded-lg bg-muted/40" />
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-orange-500"
                    checked={selectedInstPhotos.has(lightboxPhoto.id)}
                    onChange={() => toggleInstPhoto(lightboxPhoto.id)}
                    data-testid="lightbox-select-checkbox" />
                  Selecteren voor klantmap (Project {lightboxPhoto.project || 1})
                </label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadInstPhoto(lightboxPhoto)} data-testid="lightbox-download-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Downloaden
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setLightboxPhoto(null)} data-testid="lightbox-close-btn">
                    Sluiten
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bedankt mail na installatie */}
      <Dialog open={thanksDialogOpen} onOpenChange={setThanksDialogOpen}>
        <DialogContent className="max-w-md" data-testid="thanks-mail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-orange-500" />
              Bedankt mail na installatie
            </DialogTitle>
            <DialogDescription>
              De foto's staan in de klantmap. Wil je nu de "Bedankt mail na installatie" klaarzetten voor deze klant?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setThanksDialogOpen(false)} data-testid="thanks-mail-skip-btn">
              Niet nu
            </Button>
            <Button onClick={sendThanksMail} disabled={sendingThanks} data-testid="thanks-mail-send-btn">
              {sendingThanks ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
              Mail klaarzetten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5" />
              Notities - {data?.customer?.first_name} {data?.customer?.last_name}
            </DialogTitle>
            <DialogDescription>
              Voeg notities toe over deze klant
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Textarea
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              placeholder="Typ hier notities over de klant... (bv. bijzonderheden, voorkeuren, etc.)"
              className="min-h-[200px]"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={saveCustomerNotes} disabled={savingNotes}>
              {savingNotes ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Opslaan...
                </>
              ) : (
                'Opslaan'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox/Viewer - Using Portal to ensure it's above everything */}
      {viewingPhoto && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 bg-black flex items-center justify-center"
          style={{ zIndex: 99999 }}
          onClick={() => setViewingPhoto(null)}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 p-3 bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors"
            style={{ zIndex: 100000 }}
            onClick={(e) => { e.stopPropagation(); setViewingPhoto(null); }}
          >
            <X className="w-8 h-8" />
          </button>
          
          {/* Image container with zoom */}
          <div 
            className="w-full h-full flex items-center justify-center p-4 overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={viewingPhoto} 
              alt="Foto bekijken" 
              className="max-w-full max-h-full object-contain cursor-zoom-in transition-transform duration-300"
              style={{ 
                transformOrigin: 'center center'
              }}
              onClick={(e) => {
                e.stopPropagation();
                const img = e.target;
                if (img.dataset.zoomed === 'true') {
                  img.style.transform = 'scale(1)';
                  img.style.cursor = 'zoom-in';
                  img.dataset.zoomed = 'false';
                } else {
                  img.style.transform = 'scale(2.5)';
                  img.style.cursor = 'zoom-out';
                  img.dataset.zoomed = 'true';
                }
              }}
              onError={(e) => {
                e.target.src = '';
                e.target.alt = 'Foto kon niet geladen worden';
              }}
            />
          </div>
          
          {/* Instructions */}
          <p className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-sm">
            Tik op foto om in/uit te zoomen • Tik buiten foto om te sluiten
          </p>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomerDetail;
